import { apiSlice } from '../api/apiSlice';
import { fetchWorldCitiesQueryFn } from '../api/fetchWorldCities';

export const shopApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFilteredShops: builder.query({
      query: (params) => ({
        url: '/shop/filter',
        params,
      }),
      providesTags: (result) => [{ type: 'Shop', id: 'LIST' }],
    }),
    getFilteredSingleScrapItem: builder.query({
      query: (params) => ({
        url: '/shopitem/filterSingleScrapItem',
        params,
      }),
      providesTags: (result) => [{ type: 'ShopItem', id: 'LIST' }],
    }),
    getFilteredSellVehicles: builder.query({
      query: (params) => ({
        url: '/sellvehicle/filter/',
        params,
      }),
      providesTags: ['ShopItems'],
    }),
    // Vehicle Connect — multi-segment filter (generalPublic / wholeseller / property / transport / recycle / service / food)
    getVehicleConnectShops: builder.query({
      query: (params) => ({
        url: '/sellvehicle/vehicleconnect-filter',
        params,
      }),
      providesTags: ['ShopItems'],
    }),
    getFilteredTransportVehicles: builder.query({
      query: (params) => ({
        url: '/tranportvehicle/filter',
        params,
      }),
      providesTags: ['ShopItems'],
    }),
    getTransportVehiclesByShopId: builder.query({
      query: (shopid) => ({
        url: '/tranportvehicle/',
        params: { shopid },
      }),
      providesTags: ['ShopItems'],
    }),
    getTransportVehicleById: builder.query({
      query: (id) => ({
        url: '/tranportvehicle/id/',
        params: { id },
      }),
      providesTags: ['ShopItems'],
    }),
    getShopCategories: builder.query({
      query: (params) => ({
        url: '/category/type',
        params,
      }),
    }),
    getShopConsumerCategories: builder.query({
      query: (params) => ({
        url: '/category/consumer',
        params,
      }),
    }),
    getWorldCities: builder.query({
      queryFn: fetchWorldCitiesQueryFn,
      keepUnusedDataFor: 60 * 60,
    }),
    getShopById: builder.query({
      query: (arg) => {
        const id = typeof arg === 'string' ? arg : arg?.id;
        const userid = typeof arg === 'string' ? undefined : arg?.userid;
        return {
          url: '/shop/id',
          params: { id, userid },
        };
      },
      providesTags: (result, error, arg) => {
        const id = typeof arg === 'string' ? arg : arg?.id;
        return [{ type: 'Shop', id }];
      },
    }),
    updateShop: builder.mutation({
      query: ({ id, data }) => ({
        url: '/shop/id',
        method: 'PUT',
        params: { id },
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Shop', id },
        { type: 'Shop', id: 'LIST' },
      ],
    }),
    createShop: builder.mutation({
      query: (data) => ({
        url: '/shop',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Shop', id: 'LIST' }],
    }),
    checkShopLimit: builder.query({
      query: (userid) => ({
        url: '/shop/check-shop-limit',
        params: { userid },
      }),
    }),
    deleteShop: builder.mutation({
      query: (id) => ({
        url: '/shop/',
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Shop', id },
        { type: 'Shop', id: 'LIST' },
      ],
    }),
    getVendorLikeDislike: builder.query({
      query: (vendorid) => ({
        url: '/likedislike/vendorid',
        params: { vendorid },
      }),
    }),
    getShopRating: builder.query({
      query: (shopid) => ({
        url: '/rating',
        params: { type: 'shop', id: shopid },
      }),
      providesTags: (result, error, shopid) => [{ type: 'ShopRating', id: shopid || 'LIST' }],
    }),
    postLikeDislike: builder.mutation({
      query: (data) => ({
        url: '/likedislike/',
        method: 'PUT',
        body: data,
      }),
    }),
    postShopRating: builder.mutation({
      query: (data) => ({
        url: '/rating/rating',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ShopRating', id: id || 'LIST' },
        { type: 'Shop', id },
        { type: 'Shop', id: 'LIST' }
      ],
    }),
    getShopRatingByUserId: builder.query({
      query: ({ type, id, userId }) => ({
        url: '/rating/rating',
        params: { type, id, userId },
      }),
      providesTags: (result, error, { id }) => [{ type: 'ShopRating', id: id || 'LIST' }],
    }),
    getSubscriptionPurchasesByShop: builder.query({
      // Do NOT set Cache-Control/Pragma headers here — they force a CORS
      // preflight and the browser can block the real GET (OPTIONS 204, no GET).
      query: (shopid) => ({
        url: '/subscriptionMarketing/purchases-by-shop',
        params: { shopid },
      }),
      providesTags: (result, error, shopid) => [
        { type: 'SubscriptionMarketing', id: shopid || 'LIST' },
        'SubscriptionMarketing',
      ],
      keepUnusedDataFor: 30,
    }),
    trackShare: builder.mutation({
      query: (data) => ({
        url: '/subscriptionMarketing/track-share',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { shopid }) => [
        { type: 'SubscriptionMarketing', id: shopid || 'LIST' }
      ],
    }),
    getUserShopsByBusinessType: builder.query({
      query: (params) => ({
        url: '/shop/user',
        params,
      }),
      providesTags: (result) =>
        result?.data?.Shops
          ? [{ type: 'Shop', id: 'LIST' }]
          : [{ type: 'Shop', id: 'LIST' }],
    }),
    getShopSuggestionsByVendorId: builder.query({
      query: (vendorid) => ({
        url: '/suggestion/getShopSuggestion/vendorid',
        params: { vendorid },
      }),
    }),
    getShopItems: builder.query({
      query: (params) => ({
        url: '/shopitem/shop/',
        params, // { shopid, page, limit }
      }),
      providesTags: (result, error, { shopid }) => [{ type: 'ShopItems', id: shopid || 'LIST' }],
    }),
    deleteShopItem: builder.mutation({
      query: (id) => ({
        url: '/shopitem/',
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: ['ShopItems'],
    }),
    createShopItem: builder.mutation({
      query: (data) => ({
        url: '/shopitem',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ShopItems'],
    }),
    updateShopItem: builder.mutation({
      query: ({ id, data }) => ({
        url: '/shopitem/id/',
        method: 'PUT',
        params: { id },
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ShopItems', id },
        'ShopItems'
      ],
    }),
    getBusinessTypeRules: builder.query({
      query: () => ({
        url: '/businesstyperules/',
      }),
    }),
    getShopItemsFilter: builder.query({
      query: () => ({
        url: '/shopitem/filter/',
      }),
    }),
    getShopItemsByName: builder.query({
      query: (params) => ({
        url: '/shopitem/filterbyname',
        params, // { itemname, businessType }
      }),
    }),
    getVehiclesByBrand: builder.query({
      query: (params) => ({
        url: '/sellvehicle/searchbybrand',
        params, // { brandName, businessType }
      }),
    }),
    getSellVehiclesByShopId: builder.query({
      query: (shopid) => ({
        url: '/sellvehicle',
        params: { shopid },
      }),
      providesTags: (result, error, shopid) => [{ type: 'ShopItems', id: shopid || 'LIST' }],
    }),
    deleteSellVehicle: builder.mutation({
      query: (id) => ({
        url: '/sellvehicle/',
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: (result, error, id) => ['ShopItems'],
    }),
    createSellVehicle: builder.mutation({
      query: (data) => ({
        url: '/sellvehicle/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ShopItems'],
    }),
    updateSellVehicle: builder.mutation({
      query: ({ id, data }) => ({
        url: '/sellvehicle/id/',
        method: 'PUT',
        params: { id },
        body: data,
      }),
      invalidatesTags: ['ShopItems'],
    }),
    getSellVehicleById: builder.query({
      query: (id) => ({
        url: '/sellvehicle/id/',
        params: { id },
      }),
    }),
    getShopItemDetail: builder.query({
      query: (id) => ({
        url: '/shopitem/id/',
        params: { id },
      }),
    }),
    getSingleItemByUser: builder.query({
      query: (userid) => ({
        url: `/shopitem/getSingleitembyuser/${userid}`,
      }),
      providesTags: (result, error, userid) => [{ type: 'ShopItems', id: `USER_${userid}` }],
    }),
    getScrapItemByUser: builder.query({
      query: (userid) => ({
        url: `/shopitem/getscrapItemByUser/${userid}`,
      }),
      providesTags: (result, error, userid) => [{ type: 'ShopItems', id: `USER_${userid}` }],
    }),
    getPropertyDetail: builder.query({
      query: (id) => ({
        url: '/property/id/',
        params: { id },
      }),
    }),
    getAllShopsByUserId: builder.query({
      query: (userid) => ({
        url: '/shop/getAllShopsByuserid',
        params: { userid },
      }),
      providesTags: [{ type: 'Shop', id: 'ALL_BY_USER' }],
    }),
    getFoodItems: builder.query({
      query: (shopid) => ({
        url: '/fooditem/',
        params: { shopid },
      }),
    }),
    getFoodItemById: builder.query({
      query: (id) => ({
        url: '/fooditem/id/',
        params: { id },
      }),
    }),
    deleteFoodItem: builder.mutation({
      query: (id) => ({
        url: '/fooditem/',
        method: 'DELETE',
        params: { id },
      }),
    }),
    updateFoodItem: builder.mutation({
      query: ({ id, data }) => ({
        url: '/fooditem/id',
        method: 'PUT',
        params: { _id: id },
        body: data,
      }),
    }),
    searchFoodItems: builder.query({
      query: (params) => ({
        url: '/fooditem/searchbydishname/',
        params, // { dishname, businessType }
      }),
    }),
    postSuggestion: builder.mutation({
      query: (data) => ({
        url: '/suggestion/',
        method: 'POST',
        body: data,
      }),
    }),

    getBusinessTypeRulesByType: builder.query({
      query: (businessType) => ({
        url: '/businesstyperules/rulebybusinesstype',
        params: { businessType },
      }),
    }),
    createTimeline: builder.mutation({
      query: (data) => ({
        url: '/timeline/',
        method: 'POST',
        body: data,
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useLazyGetFilteredShopsQuery,
  useLazyGetFilteredSingleScrapItemQuery,
  useGetFilteredSellVehiclesQuery,
  useLazyGetFilteredSellVehiclesQuery,
  useLazyGetVehicleConnectShopsQuery,
  useLazyGetFilteredTransportVehiclesQuery,
  useGetTransportVehiclesByShopIdQuery,
  useGetTransportVehicleByIdQuery,
  useLazyGetTransportVehicleByIdQuery,
  useGetShopCategoriesQuery,
  useLazyGetShopCategoriesQuery,
  useGetShopConsumerCategoriesQuery,
  useLazyGetShopConsumerCategoriesQuery,
  useGetWorldCitiesQuery,
  useGetShopByIdQuery,
  useLazyGetShopByIdQuery,
  useCreateShopMutation,
  useCheckShopLimitQuery,
  useDeleteShopMutation,
  useUpdateShopMutation,
  useGetVendorLikeDislikeQuery,
  usePostLikeDislikeMutation,
  useGetShopRatingQuery,
  usePostShopRatingMutation,
  useGetShopRatingByUserIdQuery,
  useGetSubscriptionPurchasesByShopQuery,
  useGetUserShopsByBusinessTypeQuery,
  useLazyGetUserShopsByBusinessTypeQuery,
  useGetShopSuggestionsByVendorIdQuery,
  useLazyGetShopSuggestionsByVendorIdQuery,
  useGetShopItemsQuery,
  useGetShopItemsFilterQuery,
  useLazyGetShopItemsByNameQuery,
  useLazyGetVehiclesByBrandQuery,
  useGetSellVehiclesByShopIdQuery,
  useDeleteSellVehicleMutation,
  useCreateSellVehicleMutation,
  useUpdateSellVehicleMutation,
  useGetSellVehicleByIdQuery,
  useLazyGetSellVehicleByIdQuery,
  useLazyGetShopItemDetailQuery,
  useGetShopItemDetailQuery,
  useGetSingleItemByUserQuery,
  useGetScrapItemByUserQuery,
  useLazyGetPropertyDetailQuery,
  useGetAllShopsByUserIdQuery,
  useGetFoodItemsQuery,
  useGetFoodItemByIdQuery,
  useDeleteFoodItemMutation,
  useUpdateFoodItemMutation,
  useLazySearchFoodItemsQuery,
  usePostSuggestionMutation,
  useDeleteShopItemMutation,
  useCreateShopItemMutation,
  useUpdateShopItemMutation,
  useGetBusinessTypeRulesQuery,

  useTrackShareMutation,
  useLazyGetBusinessTypeRulesByTypeQuery,
  useGetBusinessTypeRulesByTypeQuery,
  useCreateTimelineMutation,
} = shopApiSlice;
